/**
 * In-App Browser Detection Utility
 * Single source of truth for Facebook/Instagram/social webview detection.
 */

const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';

// Platform detection
export const isFacebook = /FBAN|FBAV/i.test(ua);
export const isInstagram = /Instagram/i.test(ua);
export const isLine = /Line\//i.test(ua);
export const isTwitter = /Twitter/i.test(ua);
export const isSnapchat = /Snapchat/i.test(ua);
export const isInAppBrowser = isFacebook || isInstagram || isLine || isTwitter || isSnapchat;

// OS detection
export const isAndroid = /Android/i.test(ua);
export const isIOS = /iPhone|iPad|iPod/i.test(ua);

// Android WebView detection (WebToNative, TWA, or any embedded WebView)
export const isAndroidWebView = isAndroid && (/; wv\)/.test(ua) || /WebView/.test(ua));

// Specific problematic combo
export const isFacebookAndroid = isFacebook && isAndroid;

// ── Google Play / TWA Detection ──
// Primary: capability-based (Digital Goods API availability)
// Secondary: document.referrer hint (analytics only, not routing)

/**
 * Detect if Google Play Billing is available via Digital Goods API.
 * This is the ONLY source of truth for routing to Play Billing vs Stripe.
 * Returns true only inside a TWA distributed via Google Play with billing enabled.
 * 
 * An installed PWA outside Play will return false (no Digital Goods API).
 * A normal browser will return false.
 */
let _playBillingAvailable = null; // cached result

export async function detectPlayBilling() {
  if (_playBillingAvailable !== null) return _playBillingAvailable;
  
  if (typeof window === 'undefined' || !('getDigitalGoodsService' in window)) {
    _playBillingAvailable = false;
    return false;
  }
  
  try {
    const service = await window.getDigitalGoodsService('https://play.google.com/billing');
    _playBillingAvailable = !!service;
    console.log('[BODYMAX_PLATFORM] Play Billing available:', _playBillingAvailable);
    return _playBillingAvailable;
  } catch (e) {
    console.log('[BODYMAX_PLATFORM] Play Billing not available:', e.message);
    _playBillingAvailable = false;
    return false;
  }
}

/** Secondary hint — is this session launched from a TWA? (analytics only) */
export const isTWASession = typeof document !== 'undefined' && 
  document.referrer.startsWith('android-app://');


// Webview source label
export function getWebviewSource() {
  if (isInstagram) return 'instagram';
  if (isFacebook) return 'facebook';
  if (isLine) return 'line';
  if (isTwitter) return 'twitter';
  if (isSnapchat) return 'snapchat';
  return null;
}

/**
 * Get the platform-specific instruction for opening in external browser.
 */
export function getOpenInBrowserInstruction() {
  if (isIOS) {
    if (isInstagram) return 'Tap ••• in the top right → Open in Safari';
    if (isFacebook) return 'Tap ••• at the bottom → Open in Safari';
    return 'Tap the share/menu icon → Open in Safari';
  }
  if (isAndroid) {
    if (isInstagram) return 'Tap ••• in the top right → Open in Chrome';
    if (isFacebook) return 'Tap ⋮ in the top right → Open in Chrome';
    return 'Tap the menu → Open in Chrome';
  }
  return 'Open this link in your browser for the full experience';
}

/**
 * Build a URL that attempts to open in the default browser on Android.
 * Returns null on iOS (no reliable mechanism).
 */
export function getExternalBrowserURL(url) {
  if (!url) url = typeof window !== 'undefined' ? window.location.href : '';
  if (isAndroid) {
    return `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;end`;
  }
  return null;
}

/**
 * Diagnostics snapshot — useful for logging what's happening in webviews.
 */
export function getWebviewDiagnostics() {
  return {
    isInAppBrowser,
    source: getWebviewSource(),
    platform: isIOS ? 'ios' : isAndroid ? 'android' : 'other',
    ua: ua.slice(0, 200),
    url: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    hasLocalStorage: (() => { try { localStorage.setItem('_t', '1'); localStorage.removeItem('_t'); return true; } catch { return false; } })(),
    hasSessionStorage: (() => { try { sessionStorage.setItem('_t', '1'); sessionStorage.removeItem('_t'); return true; } catch { return false; } })(),
    hasCookies: typeof navigator !== 'undefined' ? navigator.cookieEnabled : false,
    hasToken: typeof localStorage !== 'undefined' ? !!localStorage.getItem('bodymax-token') : false,
  };
}
